import { Component, OnInit, Input, SimpleChanges, OnChanges, ViewChild, ChangeDetectorRef, ElementRef, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { FormGroupDirective, UntypedFormGroup, UntypedFormControl, Validators, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { countSpaces, formatIban, getExpertMode, removeSpecialChars, toggleEditMode } from '../../../shared/utils';
import * as moment from 'moment';
import { ValidatorService } from 'angular-iban';
import { ILookupValue } from '../../models/lookupvalues';
import { CustomErrorStateMatcher } from '../../../shared/validation';
import { TranslateService } from '@ngx-translate/core';
import { PopupService } from '../../service/popup.service';
import { AdresseComponent } from '../adresse/adresse.component';
import { merge, Observable, ReplaySubject } from 'rxjs';
import { PartnerComponent } from '../partner/partner.component';
import { VersicherungsnehmerListDialogService } from '../../service/partner-list-dialog.service';
import { IAntrag } from '../../models/antrag';
import { DateService } from '../../service/date.service';
import { first } from 'rxjs/operators';
import { LookupService } from '../../service/lookup.service';
import { ActivatedRoute } from '@angular/router';
import { ShareVnDataService } from 'src/app/service/share-vn-data.service';

@Component({
  selector: 'app-antrag',
  templateUrl: './antrag.component.html',
  styleUrls: ['./antrag.component.scss']
})
export class AntragComponent implements OnInit, OnChanges, AfterViewInit {
  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  showLaufzeit = false;

  @Input()
  showWeitereWohneinheiten = false;

  @Input()
  showVersicherungsort = false;

  @Input()
  expertMode: boolean;

  @Input()
  privatperson: boolean;

  @Input()
  _geburtsdatum: Date;

  @Input()
  _beginndatum: Date;

  @Input()
  _laufzeit: any;

  @Input()
  showEinzelunternehmen = false;

  @Input()
  anredenPrivatperson = false;

  @Input()
  zahlungsart: UntypedFormControl;

  @Input()
  zahlungsarten: Observable<Array<ILookupValue>>;

  @Input()
  praemienzahlerAbweichendePersonenart = false;

  @Input()
  personenArt: UntypedFormControl;

  @Input()
  personenArten: ReplaySubject<Array<ILookupValue>>;

  @Input()
  validateAlter = (_value: Date): boolean => { return true; };

  @Input()
  offertVersicherungsnehmer: UntypedFormGroup;

  @Output()
  geburtsdatumChangedEvent = new EventEmitter<Date>();

  beginndatumMinDate: Date;
  beginndatumMaxDate: Date;

  hauptfaelligkeitTage: Map<number, string>;
  hauptfaelligkeitMonate: Map<number, string>;

  laufzeiten: Map<number, string> =
    new Map([
      [10, '10 Jahre'],
      [3, '3 Jahre']
    ]);

  zahlungsweisen: Map<number, string> =
    new Map([
      [1, 'Zahlschein'],
      [2, 'SEPA-Lastschrift']
    ]);

  versicherungsortSelect: Map<number, string> =
    new Map([
      [1, 'wie Versicherungsnehmeradresse'],
      [2, 'andere Adresse']
    ]);

  matcher = new CustomErrorStateMatcher();

  @ViewChild('_versicherungsnehmer') versicherungsnehmerComponent: PartnerComponent;
  @ViewChild('_versicherungsort') versicherungsortComponent: AdresseComponent;
  @ViewChild('_zweiteWohneinheit') zweiteWohneinheitComponent: AdresseComponent;
  @ViewChild('_dritteWohneinheit') dritteWohneinheitComponent: AdresseComponent;
  @ViewChild('_abweichendeZustelladresseAdresse') abweichendeZustelladresseAdresseComponent: AdresseComponent;
  @ViewChild('_abweichendeZustelladressePerson') abweichendeZustelladressePersonComponent: PartnerComponent;
  @ViewChild('_praemienzahler') praemienzahlerComponent: PartnerComponent;

  constructor(public translateService: TranslateService, public popupService: PopupService, private ref: ChangeDetectorRef, private route: ActivatedRoute,
    private dateService: DateService, public versicherungsnehmerListDialogService: VersicherungsnehmerListDialogService,
    public lookupService: LookupService, private elRef: ElementRef, private shareVnDataService: ShareVnDataService) {
    this.beginndatumMinDate = dateService.getVertragsBeginnDatumMinDate(getExpertMode(route));
    this.beginndatumMaxDate = dateService.getVertragsBeginnDatumMaxDate();
  }

  ngAfterViewInit(): void {
    this.versicherungsnehmerComponent.formGroup?.valueChanges.subscribe((data) => {
      this.shareVnDataService.setVersicherungsnehmerForAntragOffert(data, this.offertVersicherungsnehmer);
    });
  }

  static createFormGroup(fb: UntypedFormBuilder, expertMode: boolean) {
    return fb.group({
      beginndatum: new UntypedFormControl('', Validators.required),
      hauptfaelligkeitTag: new UntypedFormControl(''),
      hauptfaelligkeitMonat: new UntypedFormControl(''),
      laufzeit: new UntypedFormControl('', Validators.required),
      versicherungsnehmer: PartnerComponent.createFormGroup(fb, expertMode),
      abweichenderVersicherungsort: new UntypedFormControl(''),
      versicherungsort: AdresseComponent.createFormGroup(fb),
      weitereWohneinheiten: new UntypedFormControl(''),
      zweiteWohneinheit: AdresseComponent.createFormGroup(fb),
      dritteWohneinheit: AdresseComponent.createFormGroup(fb),
      abweichendeZustelladresse: new UntypedFormControl('', Validators.required),
      abweichendeZustelladresseArt: new UntypedFormControl('', Validators.required),
      abweichendeZustelladresseAdresseGroup: AdresseComponent.createFormGroup(fb),
      abweichendeZustelladressePersonGroup: PartnerComponent.createFormGroup(fb, expertMode, false, false),
      abweichenderPraemienzahler: new UntypedFormControl('', Validators.required),
      praemienzahlerPersonenArt: new UntypedFormControl('', Validators.required),
      praemienzahler: PartnerComponent.createFormGroup(fb, expertMode),
      zahlungsweise: new UntypedFormControl('', Validators.required),
      iban: new UntypedFormControl('', [Validators.required, ValidatorService.validateIban]),
    });
  }

  ngOnInit() {
    this.onValueChanged();
    this.toggleEditMode();

    this.abweichenderVersicherungsort.setValue(1);
    this.hauptfaelligkeitTage = this.fillDateMap(25);
    this.hauptfaelligkeitMonate = this.fillDateMap(12);

    if (this.personenArt) {
      this.personenArt.valueChanges.pipe(first()).toPromise().then((personenArt) => {
        this.abweichenderPraemienzahler.valueChanges.pipe(first()).toPromise().then(() => {
          this.personenArten.pipe(first()).toPromise().then((personenArten) => {
            this.praemienzahlerPersonenArt.setValue(personenArten.find(x => x.Id === personenArt.Id));
          });
        });
      });
    }
    this.iban.valueChanges.subscribe((value) => {
      this.ibanSpaceInsert(value);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showLaufzeit && changes.showLaufzeit.currentValue !== changes.showLaufzeit.previousValue ||
      changes.showWeitereWohneinheiten && changes.showWeitereWohneinheiten.currentValue !== changes.showWeitereWohneinheiten.previousValue ||
      changes.showVersicherungsort && changes.showVersicherungsort.currentValue !== changes.showVersicherungsort.previousValue) {
      this.toggleEditMode();
    }
    if (changes._beginndatum && changes._beginndatum.currentValue?.toDateString() !== changes._beginndatum.previousValue?.toDateString()) {
      this.beginndatum.setValue(moment(this._beginndatum));
      this.beginndatum.disable();
    }
    if (this._laufzeit && changes._laufzeit && changes._laufzeit.currentValue !== changes._laufzeit.previousValue && this.showLaufzeit) {
      this.laufzeit.setValue(this._laufzeit.laufzeit);
    }
  }

  get beginndatum() { return this.formGroup.get('beginndatum'); }
  get hauptfaelligkeitTag() { return this.formGroup.get('hauptfaelligkeitTag'); }
  get hauptfaelligkeitMonat() { return this.formGroup.get('hauptfaelligkeitMonat'); }
  get laufzeit() { return this.formGroup.get('laufzeit'); }
  get versicherungsnehmer() { return this.formGroup.get('versicherungsnehmer') as UntypedFormArray; }
  get abweichenderVersicherungsort() { return this.formGroup.get('abweichenderVersicherungsort'); }
  get versicherungsort() { return this.formGroup.get('versicherungsort'); }
  get weitereWohneinheiten() { return this.formGroup.get('weitereWohneinheiten'); }
  get zweiteWohneinheit() { return this.formGroup.get('zweiteWohneinheit'); }
  get dritteWohneinheit() { return this.formGroup.get('dritteWohneinheit'); }
  get abweichendeZustelladresse() { return this.formGroup.get('abweichendeZustelladresse'); }
  get abweichendeZustelladresseArt() { return this.formGroup.get('abweichendeZustelladresseArt'); }
  get abweichendeZustelladresseAdresseGroup() { return this.formGroup.get('abweichendeZustelladresseAdresseGroup'); }
  get abweichendeZustelladressePersonGroup() { return this.formGroup.get('abweichendeZustelladressePersonGroup'); }
  get abweichenderPraemienzahler() { return this.formGroup.get('abweichenderPraemienzahler'); }
  get praemienzahlerPersonenArt() { return this.formGroup.get('praemienzahlerPersonenArt'); }
  get praemienzahler() { return this.formGroup.get('praemienzahler'); }
  get zahlungsweise() { return this.formGroup.get('zahlungsweise'); }
  get iban() { return this.formGroup.get('iban'); }

  get showHauptfaelligkeit() {
    return this.expertMode === true;
  }
  get showWeitereWohneinheitenGroup() {
    return this.weitereWohneinheiten.value === 'ja';
  }
  get showVersicherungsortGroup() {
    return this.abweichenderVersicherungsort.value === 2;
  }
  get showIban() {
    return this.zahlungsweise.value === 2;
  }
  get showAbweichendeZustelladresseArt() {
    return this.abweichendeZustelladresse.value === 'ja' ? true : false;
  }
  get showAbweichendeZustelladresseAdresseGroup() {
    return this.abweichendeZustelladresseArt.value === 1 ? true : false;
  }
  get showAbweichendeZustelladressePersonGroup() {
    return this.abweichendeZustelladresseArt.value === 2 ? true : false;
  }
  get showPraemienzahler() {
    return this.abweichenderPraemienzahler.value === 'ja' ? true : false;
  }
  get showPraemienzahlerPersonenart() {
    return this.showPraemienzahler && this.praemienzahlerAbweichendePersonenart;
  }
  get isPraemienzahlerPrivatperson() {
    return !this.praemienzahlerPersonenArt.value ? this.privatperson : this.praemienzahlerPersonenArt.value.Key === '0' ? true : false;
  }
  get isPraemienzahlerEinpersonenUnternehmen() {
    return !this.praemienzahlerPersonenArt.value ? null : this.praemienzahlerPersonenArt.value.Key === '1' ? true : false;
  }

  toggleEditMode(): void {
    toggleEditMode(this.hauptfaelligkeitTag, this.showHauptfaelligkeit);
    toggleEditMode(this.hauptfaelligkeitMonat, this.showHauptfaelligkeit);
    toggleEditMode(this.laufzeit, this.showLaufzeit);
    toggleEditMode(this.weitereWohneinheiten, this.showWeitereWohneinheiten);
    toggleEditMode(this.abweichenderVersicherungsort, this.showVersicherungsort);
    toggleEditMode(this.iban, this.showIban);
    toggleEditMode(this.praemienzahlerPersonenArt, this.showPraemienzahlerPersonenart);
    toggleEditMode(this.abweichendeZustelladresseArt, this.showAbweichendeZustelladresseArt);
  }

  getHauptfaelligkeitMonat(): number {
    return this.beginndatum.value.toDate().getDate() === 1 ?
      this.beginndatum.value.toDate().getMonth() + 1 : moment(this.beginndatum.value).add(1, 'M').toDate().getMonth() + 1;
  }

  onValueChanged(): void {
    this.beginndatum.valueChanges.subscribe(() => {
      if (this.expertMode) {
        this.hauptfaelligkeitTag.setValue(1);
        this.hauptfaelligkeitMonat.setValue(this.getHauptfaelligkeitMonat());
      }
    });
    this.praemienzahlerPersonenArt.valueChanges.subscribe(() => {
      this.ref.detectChanges();
    });
    merge(
      this.abweichenderPraemienzahler.valueChanges,
      this.zahlungsweise.valueChanges,
      this.abweichendeZustelladresse.valueChanges,
      this.abweichendeZustelladresseArt.valueChanges
    ).subscribe(() => {
      this.toggleEditMode();
    });
    merge(
      this.abweichendeZustelladresse.valueChanges,
      this.abweichendeZustelladresseArt.valueChanges,
      this.abweichenderPraemienzahler.valueChanges
    ).subscribe(() => {
      this.ref.detectChanges();
    });
    this.zahlungsart.valueChanges.subscribe((value) => {
      if (this.zahlungsart.value.Key === '3' || this.zahlungsart.value.Key === '4') {
        this.zahlungsweise.setValue(2);
        this.zahlungsweise.disable();
      } else {
        this.zahlungsweise.setValue(null);
        this.zahlungsweise.enable();
      }
      this.zahlungsart.setValue(value, { emitEvent: false });
    });
  }

  setAntrag(antrag: IAntrag) {
    this.beginndatum.setValue(moment(antrag.Beginndatum));
    this.hauptfaelligkeitTag.setValue(antrag.HauptfaelligkeitTag);
    this.hauptfaelligkeitMonat.setValue(antrag.HauptfaelligkeitMonat);
    this.laufzeit.setValue(antrag.Laufzeit);
    this.zahlungsweise.setValue(antrag.ZahlungsweiseId);
    this.iban.setValue(antrag.Iban);
    this.versicherungsnehmerComponent.setPartner(antrag.Versicherungsnehmer);
    this.abweichenderPraemienzahler.setValue(antrag.AbweichenderPraemienzahler === true ? 'ja' : 'nein');
    this.praemienzahlerPersonenArt.valueChanges.pipe(first()).toPromise().then(() => {
      this.personenArten.pipe(first()).toPromise().then((personenArt) => {
        if (antrag.PraemienzahlerPersonenArtId) {
          this.praemienzahlerPersonenArt.setValue(personenArt.find(x => x.Id === antrag.PraemienzahlerPersonenArtId.toString()));
        }
        if (antrag.Praemienzahler) {
          this.praemienzahlerComponent.setPartner(antrag.Praemienzahler);
        }
      });
    });
    this.abweichendeZustelladresse.setValue(antrag.AbweichendeZustelladresseAdresse || antrag.AbweichendeZustelladressePerson ? 'ja' : 'nein');
    if (antrag.AbweichendeZustelladresseAdresse) {
      this.abweichendeZustelladresseArt.setValue(1);
      this.abweichendeZustelladresseAdresseComponent.setAdresse(antrag.AbweichendeZustelladresseAdresse);
    } else if (antrag.AbweichendeZustelladressePerson) {
      this.abweichendeZustelladresseArt.setValue(2);
      this.abweichendeZustelladressePersonComponent.setPartner(antrag.AbweichendeZustelladressePerson);
    }
  }

  getAntrag(): IAntrag {
    return {
      Beginndatum: this.beginndatum.value,
      HauptfaelligkeitTag: this.hauptfaelligkeitTag.value ? this.hauptfaelligkeitTag.value : 1,
      HauptfaelligkeitMonat: this.hauptfaelligkeitMonat.value ? this.hauptfaelligkeitMonat.value
        : this.getHauptfaelligkeitMonat(),
      Laufzeit: this.laufzeit.value,
      ZahlungsweiseId: this.zahlungsweise.value,
      Iban: this.iban.value,
      Versicherungsnehmer: this.versicherungsnehmerComponent.getPartner(),
      AbweichenderPraemienzahler: this.abweichenderPraemienzahler.value === 'ja',
      PraemienzahlerPersonenArtId: this.praemienzahlerPersonenArt.value ? Number(this.praemienzahlerPersonenArt.value.Id) : null,
      Praemienzahler: this.praemienzahlerComponent.getPartner(),
      AbweichendeZustelladresseAdresse: this.abweichendeZustelladresseArt.value && this.abweichendeZustelladresseArt.value === 1 ?
        this.abweichendeZustelladresseAdresseComponent.getAdresse() : null,
      AbweichendeZustelladressePerson: this.abweichendeZustelladresseArt.value && this.abweichendeZustelladresseArt.value === 2 ?
        this.abweichendeZustelladressePersonComponent.getPartner() : null
    } as IAntrag;
  }

  fillDateMap(days: number): Map<number, string> {
    const dateMap = new Map<number, string>();

    for (let i = 1; i <= days; i++) {
      dateMap.set(i, ('0' + i).slice(-2) + '.');
    }

    return dateMap;
  }

  returnZero(): number {
    return 0;
  }

  ibanSpaceInsert(str: string) {
    const iban = this.elRef.nativeElement.querySelector('#iban');
    const position = iban?.selectionEnd;
    const previousValue = iban?.value;
    const value = removeSpecialChars(str);
    this.iban.setValue(formatIban(value), { emitEvent: false });
    if (position !== iban?.value.length) {
      const beforeCursor = previousValue.substring(0, position);
      const countPreviousSpace = countSpaces(beforeCursor);
      const countCurrentSpace = countSpaces(formatIban(beforeCursor));
      iban.selectionEnd = position + countCurrentSpace - countPreviousSpace;
    }
  }

  partnerChanged() {
    const geburtsdatum = this.versicherungsnehmer.controls['geburtsdatum'].value;
    this.geburtsdatumChangedEvent.emit(geburtsdatum);
  }

}
